import React from "react";
import {
  Search,
  MapPinned,
  Binoculars,
  ChartLine,
  Handshake,
  Images,
  BookMarked,
  Pin,
  ClipboardList,
  Map,
  SquareCode,
  Waves,
  CloudSun,
  Menu,
} from "lucide-react";

interface Application {
  id: string;
  title: Record<string, string>;
  shortTitle: Record<string, string>;
  lead: Record<string, string>;
  definition?: Record<string, string>;
  icon: string;
  url: string;
}

interface NavItem {
  id: string;
  title: Record<string, string>;
  url: string;
}

const primaryApplications: Application[] = [
  {
    id: "devices",
    title: {
      en: "Long-term monitorings",
      fr: "Suivis long-terme",
    },
    shortTitle: {
      en: "Monitoring",
      fr: "Suivis",
    },
    lead: {
      en: "Explore OREME's long-term monitoring for each of our topics.",
      fr: "Découvrez les suivis long-terme de l'OREME pour chacune de nos thématiques.",
    },
    definition: {
      en: "A  monitoring  is a long-term observational study focused on scientific questions related to the environment or the universe. Each monitoring project is based at one or more observation sites.",
      fr: "Un suivi est un projet d'observation sur le long-terme autour de questions scientifiques portant sur l'environnement ou l'univers. Chaque suivi s'appuie sur un ou plusieurs sites d'observation.",
    },
    url: "https://data.oreme.org/monitoring",
    icon: "Binoculars",
  },
  {
    id: "sites",
    title: {
      en: "Observation sites",
      fr: "Sites d'observation",
    },
    shortTitle: {
      en: "Sites",
      fr: "Sites",
    },
    lead: {
      en: "Explore the map of OREME observation sites near you or anywhere in the world.",
      fr: "Explorez la carte des sites d'observation de l'OREME autour de vous ou partout dans le monde.",
    },
    definition: {
      en: "An observation site is a geographical location where scientific observations are conducted. It may be monitored on an ad hoc basis or over the long term to provide series of measurements.",
      fr: "Un site d'observation est un lieu géographique sur lequel des observations scientifiques sont réalisées. Il peut être suivi de manière ponctuelle ou sur le long-terme pour fournir des séries de mesures.",
    },
    url: "https://data.oreme.org/sites",
    icon: "MapPinned",
  },
  {
    id: "timeseries",
    title: {
      en: "Series of measurements",
      fr: "Séries de mesures",
    },
    shortTitle: {
      en: "Measurements",
      fr: "Mesures",
    },
    lead: {
      en: "Visualize OREME's observational data series.",
      fr: "Visualisez les séries de données d'observation de l'OREME.",
    },
    definition: {
      en: "Measurement series enable scientists to track long-term changes in the environment, thereby gaining a better understanding of how it functions and the impacts of global changes—particularly climate change—on it.",
      fr: "Les séries de mesures permettent aux scientifiques de suivre l'évolution de l'environnement sur le long-terme, pour mieux comprendre son fonctionnement et les impacts des changements globaux, notamment du changement climatique, sur celui-ci.",
    },
    url: "https://data.oreme.org/graphs/custom_graphs/oreme_pub",
    icon: "ChartLine",
  },
  {
    id: "projects",
    title: {
      en: "Citizen science",
      fr: "Science participative",
    },
    shortTitle: {
      en: "Citizen science",
      fr: "Science participative",
    },
    lead: {
      en: "Join us in monitoring the environment with OREME!",
      fr: "Prenez part à l'observation de l'environnement avec l'OREME !",
    },
    definition: {
      en: "Citizen science, also known as participatory science or collaborative science, refers to scientific projects to which all citizens—whether experts or amateurs—can contribute. These projects invite everyone to collect and share observations.",
      fr: "Les sciences participatives sont des projets scientifiques auxquels tou•tes les citoyen•nes, spécialistes ou amateur•rices, peuvent contribuer. Ces projets proposent à toutes et tous de collecter et de partager des observations.",
    },
    url: "https://data.oreme.org/projects",
    icon: "Handshake",
  },
  {
    id: "gallery",
    title: {
      en: "Photographic galleries and collections",
      fr: "Galeries photographiques et collections",
    },
    shortTitle: {
      en: "Galleries",
      fr: "Galeries",
    },
    lead: {
      en: "Dive into the collections and the lens of OREME’s photographers!",
      fr: "Plongez dans les collections et l'oeil des photographes de l'OREME !",
    },
    definition: {
      en: "These photo galleries and collections have been compiled by teams from OREME's partner research units.",
      fr: "Ces galeries et collections photographiques et collections sont constituées par des équipes des unités de recherche partenaires de l'OREME.",
    },
    icon: "Images",
    url: "https://data.oreme.org/gallery",
  },
  {
    id: "glossary",
    title: {
      en: "Glossary of terms and keywords used",
      fr: "Lexique des termes et mots-clés employés",
    },
    shortTitle: {
      en: "Glossary",
      fr: "Glossaire",
    },
    lead: {
      en: "Learn more about the terms and keywords used within OREME.",
      fr: "Apprenez-en plus sur les termes et les mots-clés utilisés au sein de l'OREME.",
    },
    definition: {
      en: "This glossary provides definitions drawn from various sources, primarily Wikipedia and the GEMET environmental thesaurus.",
      fr: "Ce glossaire propose des définitions issues de différentes sources dont principalement Wikipédia et le thésaurus environnemental GEMET.",
    },
    icon: "BookMarked",
    url: "https://data.oreme.org/glossary",
  },
];

const secondaryApplications: Application[] = [
  {
    id: "osu",
    title: {
      en: "OSU Map",
      fr: "Carte des OSU",
    },
    shortTitle: {
      en: "OSU Map",
      fr: "Carte des OSU",
    },
    lead: {
      en: "Map of Observatories for the Sciences of the Universe.",
      fr: "Carte des Observatoires des Sciences de l'Univers.",
    },
    icon: "Pin",
    url: "https://data.oreme.org/osu_map",
  },
  {
    id: "catalog",
    title: {
      en: "Data catalog",
      fr: "Catalogue des données",
    },
    shortTitle: {
      en: "Data catalog",
      fr: "Catalogue des données",
    },
    lead: {
      en: "Detailed ans standardized inventory of OREME data.",
      fr: "Inventaire détaillé et standardisé des données de l'OREME.",
    },
    icon: "ClipboardList",
    url: "https://sig.oreme.org/geonetwork",
  },
  {
    id: "geoservices",
    title: {
      en: "Geoservices",
      fr: "Géoservices",
    },
    shortTitle: {
      en: "Géoservices",
      fr: "Géoservices",
    },
    lead: {
      en: "Distribution of geographic data in WMS and WFS formats.",
      fr: "Diffusion de données géographiques au format WMS et WFS.",
    },
    icon: "Map",
    url: "https://data.oreme.org/tools/geoservices",
  },
  {
    id: "repository",
    shortTitle: {
      en: "R tools",
      fr: "Outils R",
    },
    title: {
      en: "R tools",
      fr: "Outils R",
    },
    lead: {
      en: "Open-source utility codes in R.",
      fr: "Codes utilitaires open-source en R.",
    },
    icon: "SquareCode",
    url: "https://data.oreme.org/tools/R",
  },
  {
    id: "water",
    title: {
      en: "Water courses",
      fr: "Cours d'eau",
    },
    shortTitle: {
      en: "Water courses",
      fr: "Cours d'eau",
    },
    lead: {
      en: "Search and identify hydrographic features in France.",
      fr: "Recherche et identification d'entités hydrographiques en France.",
    },
    icon: "Waves",
    url: "https://data.oreme.org/tools/carthage",
  },
  {
    id: "weather",
    title: {
      en: "Weather stations",
      fr: "Stations Météo-France",
    },
    shortTitle: {
      en: "Weather stations",
      fr: "Stations Météo-France",
    },
    lead: {
      en: "Map of Météo-France weather stations.",
      fr: "Visualisation cartographique des stations Météo-France.",
    },
    icon: "CloudSun",
    url: "https://data.oreme.org/weather",
  },
];

const userNavItems: NavItem[] = [
  {
    id: "profile",
    title: {
      en: "Profile",
      fr: "Profil",
    },
    url: "https://data.oreme.org/users/info",
  },
  {
    id: "graphs",
    title: {
      en: "My graphs",
      fr: "Mes graphiques",
    },
    url: "https://data.oreme.org/graphs/my_graph_pages",
  },
];

const ICONS: Record<string, React.ElementType> = {
  Search,
  MapPinned,
  Binoculars,
  ChartLine,
  Handshake,
  Images,
  BookMarked,
  Pin,
  ClipboardList,
  Map,
  SquareCode,
  Waves,
  CloudSun,
  Menu,
};

const applications = [...primaryApplications, ...secondaryApplications];

const getApplication = (application: string) => {
  return applications.find((item) => item.id === application);
};

export {
  type Application,
  primaryApplications,
  secondaryApplications,
  applications,
  userNavItems,
  getApplication,
  ICONS,
};
